import { ReactElement, useState } from 'react';
import toast from 'react-hot-toast';
import OptionSection from './OptionSection';

interface DataSourceSectionProps {
  session: any;
  updateSession: any;
  invalidateSession: () => Promise<void>;
}

/**
 * Session-level data source for device runs — mirrors the run-level
 * "Reprocess from Blob Data" flag at session scope. File upload (default):
 * the device zip is authoritative and the chunk pipeline only backfills
 * linkage. Chunk upload: runs are created from the live blob stream and
 * seeded to process from chunks. Applies to runs registered from now on;
 * existing runs keep their per-run flag (switchable from the run page).
 */
export default function DataSourceSection({
  session,
  updateSession,
  invalidateSession,
}: DataSourceSectionProps): ReactElement {
  const [isUpdating, setIsUpdating] = useState(false);
  const chunkUpload = session?.reprocess_from_chunks === true;

  const handleChange = async (nextChunkUpload: boolean) => {
    if (nextChunkUpload === chunkUpload || isUpdating) {
      return;
    }
    try {
      setIsUpdating(true);
      await updateSession.mutateAsync({ reprocess_from_chunks: nextChunkUpload });
      toast.success(`Data source set to ${nextChunkUpload ? 'chunk upload' : 'file upload'}`);
      invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error updating data source');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <OptionSection
      title="Data Source"
      status={
        <span className="cr-text-sm cr-text-gray-500">{chunkUpload ? 'chunk upload' : 'file upload'}</span>
      }
    >
      <div className="cr-mb-4 cr-flex cr-flex-col cr-gap-3 cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md">
        <div className="cr-flex cr-flex-col cr-gap-2">
          <label className={`cr-flex cr-items-center ${isUpdating ? 'cr-opacity-50' : 'cr-cursor-pointer'}`}>
            <input
              type="radio"
              name="sessionDataSource"
              value="file"
              checked={!chunkUpload}
              onChange={() => handleChange(false)}
              disabled={isUpdating}
              className="cr-mr-2"
            />
            <span>
              File Upload <span className="cr-italic cr-text-gray-500">(default — device zip is authoritative)</span>
            </span>
          </label>
          <label className={`cr-flex cr-items-center ${isUpdating ? 'cr-opacity-50' : 'cr-cursor-pointer'}`}>
            <input
              type="radio"
              name="sessionDataSource"
              value="chunks"
              checked={chunkUpload}
              onChange={() => handleChange(true)}
              disabled={isUpdating}
              className="cr-mr-2"
            />
            <span>
              Chunk Upload{' '}
              <span className="cr-italic cr-text-gray-500">(runs created from the live blob stream)</span>
            </span>
          </label>
        </div>
        <p className="cr-text-xs cr-text-gray-500">
          Applies to runs registered from now on. Existing runs keep their per-run setting — switch an
          individual run with the &ldquo;Reprocess from Blob Data&rdquo; action on the run page.
        </p>
      </div>
    </OptionSection>
  );
}
